'use client'
import * as React from 'react'

import {HybridWorksData} from 'content/contentTypes'
import classes from './styles.module.css'
import GradientText from 'components/gradient-text/GradientText'
import clsx from 'clsx'
import Image from 'next/image'
import {motion} from 'framer-motion'
import {Swiper, SwiperSlide, useSwiper} from 'swiper/react'
import {EffectFade, Autoplay} from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/effect-fade'

interface HorizontalCarouselProps {
  data: HybridWorksData
}
export default function HorizontalCarousel({data}: HorizontalCarouselProps) {
  const [activeSlide, setActiveSlide] = React.useState<number>(0)

  const handleIndexChange = () => {
    if (activeSlide === data.slides.length - 1) {
      setActiveSlide(0)
    } else {
      setActiveSlide(prev => prev + 1)
    }
  }
  return (
    <div className={classes.container}>
      <div className={classes.subContainer}>
        <GradientText text={data.topTitle} type="secondary" customStyle={{textAlign: 'center'}} />
        <h2 className={clsx('header2', classes.header)}>{data.title}</h2>
        <p className={classes.desc}> {data.desc}</p>
        <div className={classes.detailsContainer}>
          <Swiper
            noSwiping={true}
            allowTouchMove={false}
            className="mySwiper"
            loop={true}
            effect={'fade'}
            autoplay={{
              delay: 3750,
              disableOnInteraction: true,
            }}
            pagination={{
              clickable: false,
            }}
            modules={[Autoplay, EffectFade]}
          >
            {data.slides.map((card, idx) => (
              <SwiperSlide key={idx}>
                <div key={card.btnDetails.desc} className={classes.singleCardContainer}>
                  <div className={classes.firstSection}>
                    <div className={classes.firstSectionBlurBg}> </div>
                    <div className={classes.textContainer}>
                      <div>
                        <span className={classes.cardTopTitle}>{card.slideDetails.topTitle}</span>
                      </div>
                      <h3 className={classes.cardTitle}>{card.slideDetails.title}</h3>
                      <div className={classes.tagContainer}>
                        {card.slideDetails.tags.map(tag => (
                          <div className={classes.tag} key={tag}>
                            {tag}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageContainer}>
                    <div
                      className={classes.imageSubContainer}
                      style={{background: card.slideDetails.imgBg}}
                    >
                      <Image
                        className={classes.cardImg}
                        src={card.slideDetails.imgSrc}
                        alt="ZenAdmin works"
                        height={650}
                        width={650}
                      />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
            <SlideBtn
              data={data}
              setActiveSlide={setActiveSlide}
              activeSlide={activeSlide}
              handleIndexChange={handleIndexChange}
            />
          </Swiper>
        </div>
      </div>
    </div>
  )
}

interface ISlideBtn {
  data: HybridWorksData
  setActiveSlide: any
  activeSlide: any
  handleIndexChange: any
}
const SlideBtn = ({data, setActiveSlide, activeSlide, handleIndexChange}: ISlideBtn) => {
  const swiper = useSwiper()
  return (
    <div className={classes.progressContainer}>
      {data.slides.map((progress, index) => (
        <div
          key={progress.btnDetails.text}
          className={classes.singleProgressContainer}
          onClick={() => {
            setActiveSlide(index)
            swiper.slideTo(index)
            swiper.autoplay.start()
          }}
        >
          {activeSlide === index ? (
            <LoaderBar handleIndexChange={handleIndexChange} />
          ) : activeSlide > index ? (
            <div className={classes.progressBarContainer}>
              <div className={classes.progressBar} />
              <div className={classes.progressBarPseudo}></div>
            </div>
          ) : (
            <div className={classes.progressBarContainer}>
              <div className={classes.progressBar} />
            </div>
          )}
          <div className={classes.progressText}>{progress.btnDetails.text}</div>
          <div className={classes.progressDesc}> {progress.btnDetails.desc}</div>
        </div>
      ))}
    </div>
  )
}
const LoaderBar = ({handleIndexChange}: {handleIndexChange: () => void}) => {
  React.useEffect(() => {
    const interval = setInterval(() => {
      handleIndexChange()
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className={classes.progressBarContainer}>
      <div className={classes.progressBar} />
      <motion.div
        className={classes.progressBarPseudo}
        animate={{
          width: ['0%', '100%'],
        }}
        transition={{
          duration: 4,
          ease: 'easeIn',
        }}
      ></motion.div>
    </div>
  )
}
